import 'temporal-polyfill/global';

import * as countriesAndTimezones from 'countries-and-timezones';
import type { IsoDate } from './date';

/*
{
  "Africa/Abidjan": {
    "name": "Africa/Abidjan",
    "countries": [
      "CI", "BF", "GH",
      "GM", "GN", "ML",
      "MR", "SH", "SL",
      "SN", "TG"
    ],
    "utcOffset": 0,
    "utcOffsetStr": "+00:00",
    "dstOffset": 0,
    "dstOffsetStr": "+00:00",
    "aliasOf": null
  },
  ...
}
*/

export function getTimezoneOptions() {
  const timezones = countriesAndTimezones.getAllTimezones();
  return Object.values(timezones).map((timezone) => ({
    id: timezone.name,
    label: timezone.name + ' (' + timezone.utcOffsetStr + ')',
  }));
}

export function getTimezone(timezoneName: string) {
  const timezone = countriesAndTimezones.getTimezone(timezoneName);
  return timezone;
}

export function getEnvironmentTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
}

/**
 * Returns the current local hour (0–23) in `timezoneName` at `now`.
 * @param timezoneName IANA timezone name.
 * @param now Instant to evaluate (defaults to wall clock).
 * @returns Local hour, or `null` when the timezone is unknown/invalid.
 */
export function getLocalHourInTimezone(
  timezoneName: string,
  now: Date = new Date(),
): number | null {
  try {
    const timezone = countriesAndTimezones.getTimezone(timezoneName);
    if (!timezone) {
      return null;
    }

    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezoneName,
      hour: 'numeric',
      hour12: false,
    });

    // Some engines emit "24" for midnight under hour12: false.
    const hourInTimezone = parseInt(formatter.format(now), 10) % 24;
    if (Number.isNaN(hourInTimezone)) {
      return null;
    }
    return hourInTimezone;
  } catch {
    return null;
  }
}

/**
 * Check if it's currently a specific hour in the given timezone.
 * @param timezoneName IANA timezone name.
 * @param hour Hour of day (0–23) to match exactly.
 * @param now Instant to evaluate (defaults to wall clock).
 * @returns True when the local hour equals `hour`.
 */
export function isHourInTimezone(
  timezoneName: string,
  hour: number,
  now: Date = new Date(),
): boolean {
  const hourInTimezone = getLocalHourInTimezone(timezoneName, now);
  return hourInTimezone !== null && hourInTimezone === hour;
}

/**
 * True when the local hour in `timezoneName` is at or after `hour` (same calendar day).
 * Used by catch-up sweeps so a 6-hour cron (or a missed tick) still processes a timezone
 * any time after its daily close hour, not only during that exact hour.
 * @param timezoneName IANA timezone name.
 * @param hour Earliest local hour (0–23) that qualifies.
 * @param now Instant to evaluate (defaults to wall clock).
 * @returns True when the local hour is at or after `hour`.
 */
export function isLocalHourAtOrAfter(
  timezoneName: string,
  hour: number,
  now: Date = new Date(),
): boolean {
  const hourInTimezone = getLocalHourInTimezone(timezoneName, now);
  return hourInTimezone !== null && hourInTimezone >= hour;
}

/**
 * Get all timezone names where it's currently the specified hour.
 */
export function getTimezonesAtHour(hour: number): string[] {
  const allTimezones = countriesAndTimezones.getAllTimezones();
  const timezonesAtHour: string[] = [];

  for (const timezone of Object.values(allTimezones)) {
    // Skip deprecated and alias timezones
    if (timezone.deprecated || timezone.aliasOf) {
      continue;
    }

    if (isHourInTimezone(timezone.name, hour)) {
      timezonesAtHour.push(timezone.name);
    }
  }

  return timezonesAtHour;
}

/**
 * Instant of local midnight (start of the current local calendar day) in `timezoneName`.
 * @param timezoneName IANA timezone name.
 * @param now Instant whose local calendar day is used (defaults to wall clock).
 * @returns UTC `Date` for that local midnight, or UTC midnight of `now` if the timezone is invalid.
 */
export function getStartOfLocalDay(timezoneName: string, now: Date = new Date()): Date {
  try {
    const instant = Temporal.Instant.fromEpochMilliseconds(now.getTime());
    const zoned = instant.toZonedDateTimeISO(timezoneName);
    return new Date(zoned.startOfDay().epochMilliseconds);
  } catch {
    const utcMidnight = new Date(now);
    utcMidnight.setUTCHours(0, 0, 0, 0);
    return utcMidnight;
  }
}

/**
 * Get the start of the previous day in the given timezone.
 * @param timezoneName IANA timezone name.
 * @param now Instant whose local calendar day is used (defaults to wall clock).
 * @returns UTC `Date` for local midnight of the previous calendar day; falls back to 24h ago.
 */
export function getPreviousDayStart(timezoneName: string, now: Date = new Date()): Date {
  try {
    const instant = Temporal.Instant.fromEpochMilliseconds(now.getTime());
    const zoned = instant.toZonedDateTimeISO(timezoneName);
    const previousDayStart = zoned.startOfDay().subtract({ days: 1 });
    return new Date(previousDayStart.epochMilliseconds);
  } catch {
    return new Date(now.getTime() - 24 * 60 * 60 * 1000);
  }
}

/**
 * Calculates the UTC offset for a specific timezone and date, accounting for DST.
 * Uses the countries-and-timezones package to get both standard and DST offsets,
 * then determines which one applies for the given date.
 * @param timezone The timezone name (e.g., "America/Los_Angeles")
 * @param date The date in IsoDate format (e.g., "2025-10-06")
 * @returns The UTC offset string (e.g., "-07:00" for PDT, "-08:00" for PST)
 */
export function getUtcOffsetForDate(timezone: string, date: IsoDate): string {
  try {
    const timezoneData = countriesAndTimezones.getTimezone(timezone);

    if (!timezoneData) {
      console.warn(`Timezone ${timezone} not found, defaulting to UTC`);
      return '+00:00';
    }

    // If timezone doesn't observe DST, use the standard offset
    if (timezoneData.utcOffset === timezoneData.dstOffset) {
      return timezoneData.utcOffsetStr;
    }

    // Create a date object for the specific date to check if DST is in effect
    const testDate = new Date(date + 'T12:00:00');

    // Check if DST is active by comparing with a known DST period
    // For most US timezones, DST is active from March to November
    const month = testDate.getMonth() + 1; // getMonth() returns 0-11, we want 1-12

    // Simple heuristic: if month is between 3-10 (March-October), likely DST
    // This is a simplified approach - for production, you might want more sophisticated DST detection
    const isLikelyDST = month >= 3 && month <= 10;

    // Return the appropriate offset
    return isLikelyDST ? timezoneData.dstOffsetStr : timezoneData.utcOffsetStr;
  } catch (error) {
    console.warn(`Failed to get UTC offset for ${timezone} on ${date}:`, error);
    return '+00:00';
  }
}
